import { useContext, useEffect, useState, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { hasToken } from 'services/helper/cookiesHelper'
import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  Grid,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material'
import defaultAvatar from 'assets/images/default_avatar.png'
import ClockInDrawer from 'module/time-attendance/components/ClockInDrawer'
import AdxButton from 'components/custom/AdxButton'
import AdxTabsBadge from 'components/reusable/tab/AdxTabsBadge'
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import AdxDropdown from 'components/custom/AdxDropdown'
import { useMutation, useQueries } from 'react-query'
import {
  displayIndividualPayslip,
  getMonthlyPayslip,
} from 'services/api/modules/staff-management/staffManagementDashboard'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import { getCalendarToday, getDashboardApprovals, getLeaveBalance } from 'services/api/dashboard'
import { getAttendanceDashboard } from 'services/api/time-attendance'
import { format } from 'date-fns'
import { formatTimer } from 'services/helper/DateTime'

import useAttendanceTimer from '../../../module/time-attendance/hooks/useAttendanceTimer'
import useAttendancePauseState from '../../../module/time-attendance/hooks/useAttendancePauseState'

import ToApproveModal from '../components/ToApproveModal'
import ActiveRequest from '../components/ActiveRequest'
import { differenceInSeconds } from 'date-fns'
import { authContext } from 'contexts/AuthContext'
import { getActiveAppraisalResult } from 'services/api/modules/performance/appraisal/result'
import CreateRequestModal from 'module/leave/pages/createRequest/view'
import CalendarDrawer from 'module/leave/pages/calendar/components/calendarDrawer'
import { useGetBigCalendar } from 'services/api/modules/leave/calendar'
import AdxDrawerComponent from 'components/reusable/drawer/AdxDrawerComponent'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'
import {
  CalendarToday,
  EditOutlined,
  DeleteOutlined,
} from '@mui/icons-material'
import { ReactComponent as NoPending } from '../assets/no_request.svg'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import SnackbarRecruitment from 'module/recruitment/reusable/SnackbarRecruitment'
import CreateAnnouncementModal from '../components/CreateAnnouncementModal'
// import MOCK_ANNOUNCEMENTS from 'pages/announcements/view/mockData/announcementsData'
import AnnouncementDetailModal from '../../announcements/components/AnnouncementDetailModal'
import DeleteAnnouncementModal from '../../announcements/components/DeleteAnnouncementModal'
import EditAnnouncementModal from '../components/EditAnnouncementModal'
import { useGetAnnouncements, useDeleteAnnouncement } from 'services/api/announcements/announcementsHook'
import { getHrAccessToken } from 'services/session/authSession'
import {
  canUserSeeAnnouncement,
  getAnnouncementUserDepartmentIds,
  getAnnouncementUserScopeKey,
} from 'services/api/announcements/visibility'

const normalizeOutOfRadiusAction = (value) => {
  if (value === 'warn' || value === 2 || value === '2') return 'warn'
  return 'block'
}

const getShiftZoneConfig = (shift) => {
  const locationConfig = shift?.location_config
  const enabled = Boolean(locationConfig?.enabled ?? shift?.use_default_location)

  if (!enabled) return null

  return {
    enabled,
    latitude:
      locationConfig?.location_latitude ?? shift?.location_latitude ?? shift?.latitude ?? null,
    longitude:
      locationConfig?.location_longitude ?? shift?.location_longitude ?? shift?.longitude ?? null,
    radius:
      locationConfig?.allowed_radius_meters ?? shift?.allowed_radius_meters ?? shift?.radius ?? null,
    out_of_radius_action: normalizeOutOfRadiusAction(
      locationConfig?.out_of_radius_action ?? shift?.out_of_radius_action
    ),
    zone_name: locationConfig?.location_name ?? shift?.location_name ?? shift?.zone_name ?? null,
    zone_address:
      locationConfig?.location_address ?? shift?.location_address ?? shift?.zone_address ?? null,
  }
}

const Dashboard = () => {
  const { data: userData } = useContext(authContext).auth
  const navigate = useNavigate()
  const hr_access_token = getHrAccessToken()
  const location = useLocation()

  const hasRole = userData?.staff?.roles
  // const isAdminOrSuperAdmin = Boolean(userData?.super_admin || userData?.permissions?.length)
  const canManageAnnouncements = Boolean(
    userData?.super_admin ||
    userData?.permissions?.includes(21) // manage_announcements permission ID
  )
  const userDepartmentIds = getAnnouncementUserDepartmentIds(userData)
  const announcementScopeKey = getAnnouncementUserScopeKey(userData)

  const theme = useTheme()
  const match_sm = useMediaQuery(theme.breakpoints.up('sm'))
  const match_mobile_m = useMediaQuery(theme.breakpoints.up('mobile_m'))
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'))

  const staffId = userData?.staff?.id

  useEffect(() => {
    if (!hasToken()) {
      navigate('/login')
    }
  }, [navigate, hr_access_token])

  const [selectedPayslip, setSelectedPayslip] = useState({})
  const [tabIndex, setTabIndex] = useState(0)
  const [approvalItem, setApprovalItem] = useState(null)

  const handleClose = () => {
    setApprovalItem(null)
  }

  const { mutate: downloadPayslip, isLoading: downloadPayslipIsLoading } = useMutation(
    (payload) => displayIndividualPayslip(payload),
    {
      onSuccess: (res) => {
        window.open(res?.data?.data?.attachment, '_blank')
      },
      onError: (error) => {
        console.error(error)
      },
    }
  )

  // const currentDateObject = startOfWeek(new Date(), { weekStartsOn: 1 })
  // const convertedStartDate = format(currentDateObject, 'yyyy-MM-dd')
  // const convertedEndDate = format(currentDateObject, 'yyyy-MM-dd')
  const today = new Date();
  const convertedStartDate = format(today, 'yyyy-MM-dd');
  const convertedEndDate = convertedStartDate;

  const results = useQueries([


    {
      queryKey: ['getAttendanceDashboard', staffId, convertedStartDate, convertedEndDate],
      queryFn: async () => {
        try {
          const { data } = await getAttendanceDashboard(convertedStartDate, convertedEndDate)
          return data
        } catch (error) {
          console.error('Error fetching attendance data:', error)
          return {
            attendanceToday: {
              attendance: {
                clockin: null,
                clockout: null,
              },
            },
          }
        }
      },
      enabled: !!staffId,
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000,
      retry: (failureCount, error) => {
        if (error?.response?.status >= 400) return false
        return failureCount < 2
      },
    },
    {
      queryKey: ['getLeaveBalance', staffId],
      // queryFn: async () => {
      //   try {
      //     const { data } = await getLeaveBalance()
      //     return data
      //   } catch (error) {
      //     console.error('Error fetching leave balance:', error)
      //     return null
      //   }
      // },
      // retry: (failureCount, error) => {
      //   if (error?.response?.status >= 400) {
      //     return false
      //   }
      //   return failureCount < 2
      // },
      queryFn: async () => {
        const { data } = await getLeaveBalance()
        return data
      },
      enabled: !!staffId,
      retry: (failureCount, error) => {
        if (error?.response?.status >= 400) {
          return false
        }
        return failureCount < 2
      },
    },
    {
      queryKey: ['getActiveAppraisal', staffId],
      queryFn: async () => {
        try {
          const { data } = await getActiveAppraisalResult(staffId)
          return data.data
        } catch (error) {
          console.error('Error fetching appraisal data:', error)
          // Return null for "No Appraisal attached" scenarios
          return null
        }
      },
      enabled: !!staffId,
      retry: (failureCount, error) => {
        // Don't retry if it's a known "no appraisal" error or client/server errors
        if (error?.response?.status >= 400) {
          return false
        }
        return failureCount < 2
      },
    },
    {
      queryKey: ['getDashboardApprovals'],
      queryFn: async () => {
        const { data } = await getDashboardApprovals()
        return data.approvals
      },
    },
    {
      queryKey: ['getCalendarToday'],
      queryFn: async () => {
        const { data } = await getCalendarToday()
        return data
      },
    },
    {
      queryKey: ['getMonthlyPayslip'],
      queryFn: async () => {
        const { data } = await getMonthlyPayslip()
        const monthMap = [
          '',
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ]
        return data.map(({ month, ...rest }) => ({
          month: monthMap[+month] || month,
          ...rest,
        }))
      },
      onError: (error) => {
        console.error(error)
      },
    },
  ])

  const [
    attendanceQuery,
    leaveBalanceQuery,
    appraisalQuery,
    approvalsQuery,
    calendarQuery,
    payslipQuery,
  ] = results

  const announcementsQuery = useGetAnnouncements(1, 12, { scopeKey: announcementScopeKey })

  const timeAttendanceData = attendanceQuery.data
  const leaveBalance = leaveBalanceQuery.data
  const activeAppraisal = appraisalQuery.data
  const dashboardApprovals = approvalsQuery.data
  const calendarToday = calendarQuery.data
  const announcementsData = announcementsQuery.data?.data?.data ?? []
  const visibleAnnouncements = Array.isArray(announcementsData)
    ? announcementsData.filter((announcement) => canUserSeeAnnouncement(announcement, userDepartmentIds))
    : []
  const announcementsList = visibleAnnouncements.slice(0, 8)
  const payslipData = payslipQuery.data

  const timeAttendanceIsLoading = attendanceQuery.isLoading && !attendanceQuery.data
  const timeAttendanceIsFetching = attendanceQuery.isFetching
  const leaveBalanceIsLoading = leaveBalanceQuery.isLoading
  const calendarTodayIsLoading = calendarQuery.isLoading
  const payslipIsLoading = payslipQuery.isLoading

  useEffect(() => {
    if (payslipData) {
      setSelectedPayslip(payslipData[0])
    }
  }, [payslipData])

  const [isOpenRequestLeaveModal, setIsOpenRequestLeaveModal] = useState(false)

  const handleOpenRequestLeaveModal = () => {
    setIsOpenRequestLeaveModal(true)
  }

  const handleCloseRequestLeaveModal = () => {
    setIsOpenRequestLeaveModal(false)
  }

  const dashboardData = [
    {
      title: 'Attendance',
      type: 'attendance',
      button: null,
      data: timeAttendanceData,
      href: null,
      isLoading: timeAttendanceIsLoading,
      isFetching: timeAttendanceIsFetching,
    },
    {
      title: 'Annual Leave Balance',
      type: 'leave',
      button: 'Request Leave',
      data: leaveBalance,
      href: null,
      isLoading: leaveBalanceIsLoading,
    },
    {
      title: 'Calendar',
      type: 'calendar',
      button: 'See More',
      data: calendarToday,
      href: '/app/leave/calendar',
      isLoading: calendarTodayIsLoading,
    },
    {
      title: 'Payslip',
      type: 'payslip',
      data: payslipData,
      isLoading: payslipIsLoading,
      subtitle: 'View & download payslip',
    },
  ]

  const dashPaySlipWorkFit = [
    // {
    //   title: 'Payslip',
    //   type: 'payslip',
    //   data: payslipData,
    //   isLoading: payslipIsLoading,
    //   subtitle: 'View and download your latest payslip',
    // },
    // {
    //   title: 'Work Personality Fit',
    //   type: 'personality',
    //   data: null,
    //   href: '/app/work-personality-fit-analysis',
    //   isLoading: calendarTodayIsLoading,
    //   subtitle: 'Explore your personality traits',
    // },
  ]

  const props = {
    selectedPayslip,
    setSelectedPayslip,
    payslipData,
    navigate,
    dashboardApprovals,
    downloadPayslip,
    downloadPayslipIsLoading,
    handleOpenRequestLeaveModal,
  }

  return (
    <DashboardContainer headerTitle="Dashboard">
      <Box sx={{
        height: { xs: 'calc(100vh - 60px)', lg: 'calc(100vh - 107px)' },
        overflowY: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}>
        <Grid container direction="column" spacing={2}>
          {/* {activeAppraisal?.length > 0 && (
            <Grid item>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderRadius: '10px',
                  width: '100%',
                  p: 2,
                  backgroundColor: 'primary.light',
                  border: 1,
                  borderColor: 'primary.main',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20px"
                    height="20px"
                    viewBox="0 0 56 56"
                  >
                    <path
                      fill="#F3BF3A"
                      d="M28 51.906c13.055 0 23.906-10.828 23.906-23.906c0-13.055-10.875-23.906-23.93-23.906C14.899 4.094 4.095 14.945 4.095 28c0 13.078 10.828 23.906 23.906 23.906m-.023-20.39c-1.243 0-1.922-.727-1.97-1.97L25.68 17.97c-.047-1.29.937-2.203 2.273-2.203c1.313 0 2.32.937 2.274 2.226l-.329 11.555c-.047 1.265-.75 1.969-1.921 1.969m0 8.625c-1.36 0-2.626-1.078-2.626-2.532s1.243-2.53 2.626-2.53c1.359 0 2.624 1.054 2.624 2.53c0 1.477-1.289 2.532-2.624 2.532"
                    />
                  </svg>
                  <Typography>You have an active appraisal</Typography>
                </Box>
                <Link to="/app/performance/appraisal">
                  <Typography
                    sx={{
                      color: 'alternate.main',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontWeight: 600,
                    }}
                  >
                    View
                  </Typography>
                </Link>
              </Box>
            </Grid>
          )} */}

          <Grid item>
            <Grid container spacing={2}>
              {dashboardData.map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item.title}>
                  <BentoCard
                    type={item.type}
                    href={item.href}
                    data={item.data}
                    isLoading={item.isLoading}
                    isFetching={item.isFetching}
                    title={item.title}
                    button={item.button}
                    props={props}
                    sx={{ height: '300px' }}
                    subtitle={item.subtitle}
                  />
                </Grid>
              ))}
              {!isLarge &&
                dashPaySlipWorkFit.map((item) => (
                  <Grid item xs={12} sm={6} md={3} key={item.title}>
                    <BentoCard
                      type={item.type}
                      href={item.href}
                      data={item.data}
                      isLoading={item.isLoading}
                      isFetching={false}
                      title={item.title}
                      button={item.button}
                      props={props}
                      sx={{ height: '300px' }}
                      subtitle={item.subtitle}
                    />
                  </Grid>
                ))}
            </Grid>
          </Grid>
          <Grid item>
            <Grid container spacing={2} mb={4}>
              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    backgroundColor: 'background.main',
                    height: {
                      xs: 'calc(100vh - 115px)',
                      xl: 'calc(100vh - 442px)',
                    },
                    padding: '20px',
                    borderRadius: '4.5px',
                    border: 1,
                    borderColor: 'grayscale.line-light',
                    overflow: 'auto',
                    '&::-webkit-scrollbar': { display: 'none' },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                  }}
                >
                  <AdxTabsBadge
                    queryParams={tabIndex}
                    setTabIndex={setTabIndex}
                    scrollButtons={false}
                  >
                    <AdxTabsPanel
                      label="Active"
                      sx={{
                        backgroundColor: 'transparent',
                        marginTop: '24px',
                        width: '100%',
                      }}
                    >
                      <ActiveRequest
                        location={location}
                        match_mobile_m={match_mobile_m}
                        match_sm={match_sm}
                        navigate={navigate}
                      />
                    </AdxTabsPanel>
                    {hasRole?.length > 0 && (
                      <AdxTabsPanel
                        label="To Approve"
                        notification={dashboardApprovals?.length}
                        sx={{
                          backgroundColor: 'transparent',
                          marginTop: '24px',
                        }}
                      >
                        {dashboardApprovals?.length > 0 ? (
                          dashboardApprovals?.map((data, index) => (
                            <ToApprove
                              key={index}
                              dashboardApprovals={dashboardApprovals}
                              data={data}
                              index={index}
                              setApprovalItem={setApprovalItem}
                            />
                          ))
                        ) : (
                          <AdxEmptyStateV2
                            height="400px"
                            icon={<NoPending width={90} height={90} />}
                            title="No approval required"
                            subtitle={'Your pending approvals will show up here'}
                          />
                        )}
                      </AdxTabsPanel>
                    )}
                  </AdxTabsBadge>
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <AnnouncementPanel
                  data={announcementsList}
                  navigate={navigate}
                  isLoading={announcementsQuery.isLoading}
                  // canCreate={isAdminOrSuperAdmin}
                  canCreate={canManageAnnouncements}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {approvalItem && (
          <ToApproveModal
            key={approvalItem.id}
            isOpen={Boolean(approvalItem.id)}
            handleClose={handleClose}
            approvalId={approvalItem.id}
            category={approvalItem.category}
            leaveRequestId={approvalItem.leave_request_id}
            leaveType={approvalItem.leave_type}
          />
        )}
        {isOpenRequestLeaveModal && (
          <CreateRequestModal
            modalOpen={isOpenRequestLeaveModal}
            setModalOpen={setIsOpenRequestLeaveModal}
            onClose={handleCloseRequestLeaveModal}
          />
        )}
      </Box>
    </DashboardContainer>
  )
}

export default Dashboard

const BentoCard = ({
  type,
  data,
  isLoading = false,
  isFetching = false,
  href,
  title,
  subtitle = null,
  button = null,
  sx = {},
  props,
}) => {
  const renderMap = {
    attendance: () => <AttendanceCardContent data={data} isLoading={isLoading} />,
    leave: () => <LeaveCardContent data={data} isLoading={isLoading} />,
    calendar: () => <CalendarCardContent data={data} isLoading={isLoading} />,
    payslip: () => <PayslipCardContent data={data} props={props} isLoading={isLoading} />,
    // personality: () => <PersonalityCardContent data={data} props={props} isLoading={isLoading} />,
  }

  const handleCardClick = () => {
    if (href) props?.navigate(href)
    if (type === 'leave') props?.handleOpenRequestLeaveModal?.()
  }

  const shouldShowInitialSkeleton = isLoading && !data

  return (
    <Box
      sx={{
        width: '100%',
        height: 'fit-content',
        padding: '20px',
        borderRadius: '4.5px',
        backgroundColor: 'background.main',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        border: 1,
        borderColor: 'grayscale.line-light',
        overflow: 'hidden',
        position: 'relative',
        ...sx,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {shouldShowInitialSkeleton ? (
          <Skeleton variant="text" width="35%" />
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
              {title}
            </Typography>

            {subtitle && (
              <Typography
                variant="subtitle1"
                sx={{
                  color: 'text.tertiary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Content */}
      {typeof renderMap[type] === 'function' ? (
        renderMap[type]()
      ) : (
        <Typography color="error.main">Unknown card type: {type}</Typography>
      )}

      {/* Footer */}
      {button && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            borderTop: 1,
            borderColor: 'gray-line.main',
            marginX: '-20px',
            marginBottom: '-20px',
            padding: '16px',
            '&:hover': { backgroundColor: 'primary.light' },
            '&:hover .arrow-icon': { transform: 'rotate(-45deg)' },
          }}
          onClick={handleCardClick}
        >
          {shouldShowInitialSkeleton ? (
            <Skeleton variant="rounded" width="50%" />
          ) : (
            <>
              <Typography variant="subtitle1">{button}</Typography>
              <Box
                sx={{
                  width: '24px',
                  height: '24px',
                  backgroundColor: 'primary.main',
                  borderRadius: '100%',
                  transition: 'transform 0.3s ease',
                }}
                className="arrow-icon"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <path
                    fill="#FFFFFF"
                    fillRule="evenodd"
                    d="M9.293 7.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414-1.414L12.586 12L9.293 8.707a1 1 0 0 1 0-1.414"
                    clipRule="evenodd"
                  />
                </svg>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Optional: show a subtle refetch indicator WITHOUT unmount */}
      {isFetching && !shouldShowInitialSkeleton && (
        <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
          <CircularProgress size={16} />
        </Box>
      )}
    </Box>
  )
}

const ToApprove = ({ dashboardApprovals, data, index, setApprovalItem }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '14px',
        paddingX: '14px',
        paddingY: '16px',
        borderRadius:
          dashboardApprovals?.length === 1
            ? '10px'
            : index === 0
              ? '10px 10px 0 0'
              : index === dashboardApprovals?.length - 1
                ? '0 0 10px 10px'
                : '',
        border: 1,
        borderTop: index === 0 ? 1 : 0,
        borderBottom: index === dashboardApprovals?.length - 1 ? '' : 1,
        borderColor: 'grayscale.line-light',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'primary.light',
        },
      }}
      onClick={() => {
        setApprovalItem({
          id: data?.id,
          category: data?.category,
          leave_request_id: data?.category === 'leave' ? data?.leave_request_id : null,
          leave_type: data?.subtitle === 'Leave Request' ? 1 : 2,
        })
      }}
    >
      <Box
        sx={{
          width: 74,
          height: 74,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            width: 46,
            height: 46,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '100%',
            backgroundColor: 'alternate.light',
            position: 'absolute',
            top: 0,
            left: 0,
            color: 'alternate.main',
          }}
        >
          {data.category === 'leave' ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M20 3h-1V2c0-.55-.45-1-1-1s-1 .45-1 1v1H7V2c0-.55-.45-1-1-1s-1 .45-1 1v1H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2m-1 18H5c-.55 0-1-.45-1-1V8h16v12c0 .55-.45 1-1 1"
              />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <g fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M7.5 5.25a3 3 0 0 1 3-3h3a3 3 0 0 1 3 3v.205q1.399.128 2.774.334c1.454.218 2.476 1.483 2.476 2.917v3.033c0 1.211-.734 2.352-1.936 2.752A24.7 24.7 0 0 1 12 15.75a24.7 24.7 0 0 1-7.814-1.259c-1.202-.4-1.936-1.541-1.936-2.752V8.706c0-1.434 1.022-2.7 2.476-2.917A49 49 0 0 1 7.5 5.455zm7.5 0v.09a50 50 0 0 0-6 0v-.09a1.5 1.5 0 0 1 1.5-1.5h3a1.5 1.5 0 0 1 1.5 1.5m-3 8.25a.75.75 0 1 0 0-1.5a.75.75 0 0 0 0 1.5"
                  clipRule="evenodd"
                />
                <path d="M3 18.4v-2.796a4.3 4.3 0 0 0 .713.31A26.2 26.2 0 0 0 12 17.25c2.892 0 5.68-.468 8.287-1.335q.378-.127.713-.311V18.4c0 1.452-1.047 2.728-2.523 2.923c-2.12.282-4.282.427-6.477.427a49 49 0 0 1-6.477-.427C4.047 21.128 3 19.852 3 18.4" />
              </g>
            </svg>
          )}
        </Box>
        <Avatar
          alt="profile"
          sx={{ width: 46, height: 46, position: 'absolute', bottom: 0, right: 0 }}
          src={data?.avatar_url || defaultAvatar}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', width: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'space-between',
            }}
          >
            <Box display={'flex'} flexDirection={'column'} gap={1}>
              <Typography variant="subtitle1">{data?.title}</Typography>
              <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={1}>
                <Typography sx={{ color: 'alternate.main' }}>{data?.subtitle}</Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ color: 'text.quaternary', display: { xs: 'none', sm: 'block' } }}
                >
                  •
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ color: 'text.quaternary', display: { xs: 'none', sm: 'block' } }}
                >
                  {data?.date_range}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.quaternary' }}>
                Requester: {data?.staff_name}
              </Typography>
            </Box>
            <Typography variant="subtitle1">{data?.badge}</Typography>
          </Box>
        </Box>
        <Box display={'flex'} alignItems={'center'}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="8"
            height="16"
            viewBox="0 0 8 16"
            fill="none"
          >
            <path
              d="M1.32922 7.85718e-05C1.49994 -0.000502201 1.66862 0.0371765 1.82287 0.110345C1.97711 0.183514 2.11301 0.290314 2.22056 0.422897L7.74004 7.27939C7.90812 7.48387 8 7.74035 8 8.00504C8 8.26973 7.90812 8.52621 7.74004 8.73068L2.02629 15.5872C1.83232 15.8205 1.55359 15.9673 1.25142 15.9952C0.949248 16.023 0.648384 15.9297 0.415015 15.7357C0.181647 15.5418 0.0348907 15.263 0.00703058 14.9609C-0.0208291 14.6587 0.0724894 14.3578 0.266458 14.1245L5.37455 7.99932L0.437872 1.87419C0.298133 1.70645 0.209368 1.50219 0.182079 1.28559C0.154791 1.06898 0.190122 0.849087 0.283891 0.651931C0.37766 0.454775 0.525943 0.288606 0.711194 0.173086C0.896446 0.0575651 1.11091 -0.00247253 1.32922 7.85718e-05Z"
              fill="#8D9593"
            />
          </svg>
        </Box>
      </Box>
    </Box>
  )
}

const AttendanceCardContent = ({ data, isLoading }) => {
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState('')
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState('')
  const shiftZone = getShiftZoneConfig(data?.shift)

  const attendanceToday = data?.attendanceToday || null
  const attendanceData = attendanceToday?.attendance || null
  const attendanceId = attendanceData?.id ?? null

  const clockinData = attendanceData?.clockin ?? null
  const clockoutData = attendanceData?.clockout ?? null

  const hasClockIn = !!clockinData?.clockin
  const hasClockOut = !!clockoutData?.clockout
  const clockinCreatedAt = clockinData?.created_at ?? null

  const apiIsPaused = !!attendanceToday?.is_paused
  const pausedAt = attendanceToday?.paused_at ?? null
  const totalPausedSeconds = Number(attendanceToday?.total_paused_seconds ?? 0)

  const [isPaused, setIsPaused] = useAttendancePauseState(attendanceToday, attendanceId)
  const [clockDrawerOpen, setClockDrawerOpen] = useState(false)

  const time = useAttendanceTimer({
    attendanceId,
    clockinCreatedAt,
    hasClockIn,
    hasClockOut,
    isPaused: (typeof isPaused === 'boolean' ? isPaused : apiIsPaused),
    pausedAt,
    totalPausedSeconds,
  })

  const shouldShowInitialSkeleton = isLoading && !data

  if (shouldShowInitialSkeleton) {
    // keep your loading skeleton unchanged
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'alternate.light',
            padding: '13px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            borderRadius: '4.5px',
          }}
        >
          <Skeleton variant="text" width={100} />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {[1, 2].map((_, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <Skeleton variant="text" width={100} />
            </Box>
          ))}
        </Box>

        <Skeleton variant="rounded" width={'100%'} height={45} sx={{ borderRadius: '500px' }} />
      </Box>
    )
  }


  if (!data) {
    // keep your "no data" UI unchanged; just pass isPaused props too (safe)
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'alternate.light',
            padding: '13px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            borderRadius: '4.5px',
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path
              fill="#2666BE"
              d="M10 3q-.425 0-.712-.288T9 2t.288-.712T10 1h4q.425 0 .713.288T15 2t-.288.713T14 3zm2 11q.425 0 .713-.288T13 13V9q0-.425-.288-.712T12 8t-.712.288T11 9v4q0 .425.288.713T12 14m0 8q-1.85 0-3.488-.712T5.65 19.35t-1.937-2.863T3 13t.713-3.488T5.65 6.65t2.863-1.937T12 4q1.55 0 2.975.5t2.675 1.45l.7-.7q.275-.275.7-.275t.7.275t.275.7t-.275.7l-.7.7Q20 8.6 20.5 10.025T21 13q0 1.85-.713 3.488T18.35 19.35t-2.863 1.938T12 22m0-2q2.9 0 4.95-2.05T19 13t-2.05-4.95T12 6T7.05 8.05T5 13t2.05 4.95T12 20m0-7"
            />
          </svg>
          <Typography variant="subtitle1" sx={{ color: 'alternate.main' }}>
            --:--
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {[
            { title: 'Clock In', time: '--:--' },
            { title: 'Clock Out', time: '--:--' },
          ].map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: 'light-gray-text-2.main', minWidth: '70px', textAlign: 'right' }}
              >
                {item.title}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: 'light-gray-text-2.main' }}>
                :
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: 'dark-text.main', minWidth: '70px', textAlign: 'left' }}
              >
                {item.time}
              </Typography>
            </Box>
          ))}
        </Box>

        <ClockInDrawer
          data={null}
          hasShift={false}
          time={time}
          fullWidth
          showPauseResumeOnButton
          isPaused={isPaused}
          setIsPaused={setIsPaused}
          setSuccessSnackbarOpen={setSuccessSnackbarOpen}
          setErrorSnackbarOpen={setErrorSnackbarOpen}
          open={clockDrawerOpen}
          setOpen={setClockDrawerOpen}
        />
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'alternate.light',
            padding: '13px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            borderRadius: '4.5px',
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path
              fill="#2666BE"
              d="M10 3q-.425 0-.712-.288T9 2t.288-.712T10 1h4q.425 0 .713.288T15 2t-.288.713T14 3zm2 11q.425 0 .713-.288T13 13V9q0-.425-.288-.712T12 8t-.712.288T11 9v4q0 .425.288.713T12 14m0 8q-1.85 0-3.488-.712T5.65 19.35t-1.937-2.863T3 13t.713-3.488T5.65 6.65t2.863-1.937T12 4q1.55 0 2.975.5t2.675 1.45l.7-.7q.275-.275.7-.275t.7.275t.275.7t-.275.7l-.7.7Q20 8.6 20.5 10.025T21 13q0 1.85-.713 3.488T18.35 19.35t-2.863 1.938T12 22m0-2q2.9 0 4.95-2.05T19 13t-2.05-4.95T12 6T7.05 8.05T5 13t2.05 4.95T12 20m0-7"
            />
          </svg>
          <Typography variant="subtitle1" sx={{ color: 'alternate.main' }}>
            {formatTimer(time)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          {[
            { title: 'Clock In', time: clockinData?.clockin ?? '--:--' },
            { title: 'Clock Out', time: clockoutData?.clockout ?? '--:--' },
          ].map((item, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: 'light-gray-text-2.main', minWidth: '70px', textAlign: 'right' }}
              >
                {item.title}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: 'light-gray-text-2.main' }}>
                :
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: 'dark-text.main', minWidth: '70px', textAlign: 'left' }}
              >
                {item.time || '--:--'}
              </Typography>
            </Box>
          ))}
        </Box>

        <ClockInDrawer
          data={data?.attendanceToday ? data?.attendanceToday?.attendance : null}
          shiftStatus={data?.shift?.status}
          hasShift={!!data?.shift}
          shiftZone={shiftZone}
          time={time}
          fullWidth
          showPauseResumeOnButton
          setSuccessSnackbarOpen={setSuccessSnackbarOpen}
          setErrorSnackbarOpen={setErrorSnackbarOpen}
          isPaused={isPaused}
          setIsPaused={setIsPaused}
          open={clockDrawerOpen}
          setOpen={setClockDrawerOpen}
        />
      </Box>

      {!!successSnackbarOpen && (
        <SnackbarRecruitment
          autoHideDuration={3000}
          status="success"
          TransitionComponent="slide"
          message={successSnackbarOpen}
          open={!!successSnackbarOpen}
          handleClose={() => setSuccessSnackbarOpen('')}
        />
      )}

      {!!errorSnackbarOpen && (
        <SnackbarRecruitment
          autoHideDuration={3000}
          status="error"
          TransitionComponent="slide"
          message={errorSnackbarOpen}
          open={!!errorSnackbarOpen}
          handleClose={() => setErrorSnackbarOpen('')}
        />
      )}
    </>
  )
}

const LeaveCardContent = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Skeleton variant="rounded" width="30%" />
        <Skeleton variant="text" width="30%" />
        <Box sx={{ position: 'relative', width: 81, height: 81 }}>
          <Skeleton variant="circular" width={81} height={81} />
        </Box>
      </Box>
    )
  }

  // Check if data and with_entitlement exist and have at least one item
  if (
    !data ||
    !data.with_entitlement ||
    !Array.isArray(data.with_entitlement) ||
    data.with_entitlement.length === 0
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          width: '100%',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'grayscale.base-background',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'text.secondary',
            borderRadius: '100%',
            width: '62px',
            height: '62px',
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="30px" height="30px" viewBox="0 0 48 48">
            <g fill="currentColor">
              <path
                fillRule="evenodd"
                d="M12 21a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2zm0 2v2h2v-2zm6 0a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2zm2 0h2v2h-2zm8-2a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2zm0 2v2h2v-2zm-18 8a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2zm4 0v2h-2v-2zm6-2a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2zm2 2h-2v2h2z"
                clipRule="evenodd"
              />
              <path d="M36 32.5a1 1 0 1 0-2 0v2.914l1.293 1.293a1 1 0 0 0 1.414-1.414L36 34.586z" />
              <path
                fillRule="evenodd"
                d="M12 7a1 1 0 1 1 2 0v5a1 1 0 1 0 2 0V9h10V7a1 1 0 1 1 2 0v5a1 1 0 1 0 2 0V9h3a3 3 0 0 1 3 3v16.07A7.001 7.001 0 0 1 35 42a6.99 6.99 0 0 1-5.745-3H9a3 3 0 0 1-3-3V12a3 3 0 0 1 3-3h3zm16 28a7 7 0 0 1 6-6.93V18H8v18a1 1 0 0 0 1 1h19.29a7 7 0 0 1-.29-2m12 0a5 5 0 1 1-10 0a5 5 0 0 1 10 0"
                clipRule="evenodd"
              />
            </g>
          </svg>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
            No leave balance yet
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Your available leave balance will show up here
          </Typography>
        </Box>
      </Box>
    )
  }

  // Prefer the entitlement for "Annual Leave". Fall back to the first entry if not present.
  const entitlementArray = Array.isArray(data.with_entitlement) ? data.with_entitlement : []
  const entitlementData =
    entitlementArray.find((e) =>
      String(e?.leave_policy?.name || '')
        .toLowerCase()
        .trim() === 'annual leave'
    ) || entitlementArray[0] || {}

  const carryForwardAmount = entitlementData.carry_forward?.amount || 0
  const carryForwardBalance = entitlementData.carry_forward?.balance || 0
  const entitlementAmount = entitlementData.entitlement?.amount || 0
  const leaveBalance = entitlementData.entitlement?.balance + carryForwardBalance || 0

  const totalLeave = carryForwardAmount + entitlementAmount

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        alignSelf: 'center',
        position: 'relative',
      }}
    >
      <Box sx={{ position: 'relative', width: '150px', height: '150px' }}>
        <CircularProgress
          variant="determinate"
          size={150}
          thickness={4}
          value={100}
          sx={{ position: 'absolute', color: 'alternate.light' }}
        />
        <CircularProgress
          variant="determinate"
          size={150}
          thickness={4}
          value={(leaveBalance / totalLeave) * 100}
          sx={{
            position: 'absolute',
            color: 'alternate.main',
            zIndex: 1,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: 1,
            }}
          >
            <Typography
              variant="h2"
              sx={{ fontWeight: 800, color: 'alternate.main', fontSize: 'inherit' }}
            >
              {leaveBalance}
            </Typography>

            <Typography
              variant="h2"
              sx={{ fontWeight: 500, color: 'text.tertiary', fontSize: 'inherit' }}
            >
              /{totalLeave}
            </Typography>
          </Box>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            {leaveBalance > 1 ? 'Days' : 'Day'} Left
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

const CalendarCardContent = ({ data, isLoading }) => {
  const today = new Date()
  // const today = new Date(new Date().setDate(new Date().getDate() - 12))
  const dayName = new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(today)
  const dateOfMonth = today.getDate()

  // const [drawerData, setDrawerData] = useState(today)
  // const [tabState, setTabState] = useState(0)
  // const [drawerState, setDrawerState] = useState({ right: false })
  // const handleDrawer = (anchor, open) => () => {
  //   setDrawerState({ ...drawerState, [anchor]: open })
  // }

  // const { todayData, todayIsLoading, refetch } = useGetBigCalendar('', today)
  // const [...calendarEvent] = Object.entries(todayData?.data || {})

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', gap: '20px', justifyContent: 'space-between', width: '100%' }}>
        <Box>
          <Skeleton variant="rounded" width={15} sx={{ marginBottom: '12px' }} />
          <Skeleton variant="rounded" width={15} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
          <Skeleton variant="rounded" width={'100%'} />
          <Skeleton variant="rounded" width={'100%'} />
        </Box>
      </Box>
    )
  }

  if (!data) {
    return (
      <Box sx={{ display: 'flex', gap: '20px', justifyContent: 'space-between', width: '100%' }}>
        <Box>
          <Typography variant="h2">{dateOfMonth}</Typography>
          <Typography variant="subtitle1">{dayName}</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            width: '100%',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'grayscale.base-background',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'text.secondary',
              borderRadius: '100%',
              width: '62px',
              height: '62px',
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="30px" height="30px" viewBox="0 0 24 24">
              <g fill="none" fillRule="evenodd">
                <path d="m12.594 23.258l-.012.002l-.071.035l-.02.004l-.014-.004l-.071-.036q-.016-.004-.024.006l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.016-.018m.264-.113l-.014.002l-.184.093l-.01.01l-.003.011l.018.43l.005.012l.008.008l.201.092q.019.005.029-.008l.004-.014l-.034-.614q-.005-.019-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.003-.011l.018-.43l-.003-.012l-.01-.01z" />
                <path
                  fill="currentColor"
                  d="M5 7.5a4.5 4.5 0 1 1 9 0a4.5 4.5 0 0 1-9 0M9.5 5a2.5 2.5 0 1 0 0 5a2.5 2.5 0 0 0 0-5m0 8c1.993 0 3.805.608 5.137 1.466c.667.43 1.238.937 1.653 1.49c.407.545.71 1.2.71 1.901c0 .755-.35 1.36-.864 1.797c-.485.41-1.117.676-1.77.859c-1.313.367-3.05.487-4.866.487s-3.553-.12-4.865-.487c-.654-.183-1.286-.449-1.77-.859C2.349 19.218 2 18.612 2 17.857c0-.702.303-1.356.71-1.9c.415-.554.986-1.062 1.653-1.49C5.695 13.607 7.507 13 9.5 13m0 2c-1.597 0-3.035.492-4.055 1.148c-.51.328-.89.682-1.134 1.007c-.25.334-.311.576-.311.702c0 .074.015.15.157.27c.173.148.494.314 1.016.46c1.04.29 2.553.413 4.327.413s3.287-.123 4.327-.413c.522-.146.843-.312 1.016-.46c.142-.12.157-.196.157-.27c0-.126-.061-.368-.311-.702c-.244-.325-.624-.679-1.134-1.007C12.535 15.492 11.097 15 9.5 15m8.5-2c1.32 0 2.518.436 3.4 1.051c.822.573 1.6 1.477 1.6 2.52c0 .587-.253 1.073-.638 1.426c-.357.328-.809.528-1.244.66c-.87.263-1.99.343-3.118.343h-.203c.13-.348.203-.73.203-1.143q-.002-.336-.06-.65L17.893 17H18c1.081 0 1.96-.082 2.539-.257c.262-.08.397-.16.455-.206c-.029-.118-.185-.46-.738-.845a4 4 0 0 0-3.331-.546a7.5 7.5 0 0 0-1.684-1.48A6.06 6.06 0 0 1 18 13m-3-4a3 3 0 1 1 6 0a3 3 0 0 1-6 0m3-1a1 1 0 1 0 0 2a1 1 0 0 0 0-2"
                />
              </g>
            </svg>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
              No one is on leave today
            </Typography>
            <Typography
              // onClick={() => setDrawerState({ right: true })}
              variant="body2"
              sx={{ color: 'text.secondary' }}
            >
              People on leave will show up here
            </Typography>
          </Box>
        </Box>
        {/* <CalendarTodayDrawer
          date={today}
          data={data}
          state={drawerState}
          handleDrawer={handleDrawer}
        /> */}
      </Box>
    )
  }

  const dateKey = Object.keys(data)[0]
  const birthdayData = data?.[dateKey]?.birthday ?? []
  const leaveData = data?.[dateKey]?.leave_request ?? []

  return (
    <Box sx={{ display: 'flex', gap: '20px', justifyContent: 'space-between', width: '100%' }}>
      <Box>
        <Typography variant="h2">{dateOfMonth}</Typography>
        <Typography variant="subtitle1">{dayName}</Typography>
      </Box>
      <Box
        // onClick={() => setDrawerState({ right: true })}
        sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}
      >
        {birthdayData?.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              padding: '10px 12px',
              backgroundColor: 'gray-background.main',
              borderLeft: 4,
              borderColor: 'warning.main',
            }}
          >
            <Typography variant="body1">{`${birthdayData.length} Birthday${birthdayData.length > 1 ? 's' : ''
              }!`}</Typography>
            <Box sx={{ position: 'relative', width: 36, height: 20 }}>
              <Avatar
                alt="profile"
                sx={{
                  width: 20,
                  height: 20,
                  bgcolor: 'alternate.main',
                  position: 'absolute',
                  left: 0,
                  zIndex: 2,
                }}
                src={birthdayData[0].staff_image || defaultAvatar}
              />
              {birthdayData.length > 1 && (
                <Avatar
                  alt="profile"
                  sx={{
                    width: 20,
                    height: 20,
                    bgcolor: 'alternate.main',
                    position: 'absolute',
                    right: 0,
                    zIndex: 2,
                  }}
                  src={birthdayData[1].staff_image || defaultAvatar}
                />
              )}
            </Box>
          </Box>
        )}
        {leaveData?.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              padding: '10px 12px',
              backgroundColor: 'gray-background.main',
              borderLeft: 4,
              borderColor: 'error.main',
            }}
          >
            <Typography variant="body1">{leaveData.length} On Leave</Typography>
            <Box sx={{ position: 'relative', width: 36, height: 20 }}>
              <Avatar
                alt="profile"
                sx={{
                  width: 20,
                  height: 20,
                  bgcolor: 'alternate.main',
                  position: 'absolute',
                  left: 0,
                  zIndex: 2,
                }}
                src={leaveData[0].staff_image || defaultAvatar}
              />
              {leaveData.length > 1 && (
                <Avatar
                  alt="profile"
                  sx={{
                    width: 20,
                    height: 20,
                    bgcolor: 'alternate.main',
                    position: 'absolute',
                    right: 0,
                    zIndex: 2,
                  }}
                  src={leaveData[1].staff_image || defaultAvatar}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
      {/* <CalendarTodayDrawer
        date={today}
        data={data['2025-04-15']}
        state={drawerState}
        handleDrawer={handleDrawer}
      /> */}
    </Box>
  )
}
const PayslipCardContent = ({ data, isLoading, props }) => {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          paddingTop: '16px',
        }}
      >
        <Skeleton variant="circular" width={65} height={65} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
          <Skeleton variant="rounded" width={'100%'} height={54} />
          <Skeleton variant="rounded" width={'100%'} height={45} sx={{ borderRadius: '500px' }} />
        </Box>
      </Box>
    )
  }
  if (!data) {
    return null
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        paddingTop: '16px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '65px',
          height: '65px',
          borderRadius: '100%',
          backgroundColor: 'alternate.light',
          color: 'alternate.main',
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="32px" height="32px" viewBox="0 0 512 512">
          <path
            fill="currentColor"
            d="M428 224H288a48 48 0 0 1-48-48V36a4 4 0 0 0-4-4h-92a64 64 0 0 0-64 64v320a64 64 0 0 0 64 64h224a64 64 0 0 0 64-64V228a4 4 0 0 0-4-4m-92 160H176a16 16 0 0 1 0-32h160a16 16 0 0 1 0 32m0-80H176a16 16 0 0 1 0-32h160a16 16 0 0 1 0 32"
          />
          <path
            fill="currentColor"
            d="M419.22 188.59L275.41 44.78a2 2 0 0 0-3.41 1.41V176a16 16 0 0 0 16 16h129.81a2 2 0 0 0 1.41-3.41"
          />
        </svg>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
        <AdxDropdown
          value={props.selectedPayslip}
          onChange={(e) => props.setSelectedPayslip(e.target.value)}
          renderValue={(selected) => {
            return <Typography>{`${selected?.month} ${selected?.year}`}</Typography>
          }}
        >
          {props.selectedPayslip !== undefined ? (
            data?.map((selectedPayslip, index) => (
              <AdxMenuItem
                key={index}
                value={selectedPayslip}
                selectedvalue={props.selectedPayslip}
              >{`${selectedPayslip?.month} ${selectedPayslip?.year}`}</AdxMenuItem>
            ))
          ) : (
            <Box px={2} py={1}>
              <Typography color="light-gray-text.main">No payslip generated yet.</Typography>
            </Box>
          )}
        </AdxDropdown>
        <AdxButton
          variant="outlined"
          isLoading={props?.downloadPayslipIsLoading}
          onClick={() => props?.downloadPayslip({ time_id: props?.selectedPayslip?.time_id })}
          disabled={!props.selectedPayslip}
        >
          View
        </AdxButton>
      </Box>
    </Box>
  )
}

// const PersonalityCardContent = ({ props }) => {
//   return (
//     <Box
//       sx={{
//         display: 'flex',
//         flexDirection: 'column',
//         alignItems: 'center',
//         gap: '16px',
//         paddingTop: '16px',
//       }}
//     >
//       <Box
//         sx={{
//           display: 'flex',
//           justifyContent: 'center',
//           alignItems: 'center',
//           width: '65px',
//           height: '65px',
//           borderRadius: '100%',
//           backgroundColor: 'alternate.light',
//           color: 'alternate.main',
//         }}
//       >
//         <svg
//           xmlns="http://www.w3.org/2000/svg"
//           width="65"
//           height="65"
//           viewBox="0 0 65 65"
//           fill="currentColor"
//         >
//           <path
//             d="M36.2385 22.3958C34.9819 20.6919 44.0919 24.9512 44.092 30.0628C44.6156 31.4826 45.7886 34.3794 46.2912 34.6067C46.9196 34.8907 45.6628 36.8788 44.092 36.3108V40.0022C43.3588 41.0435 41.2643 42.9552 38.7512 42.2737V45.6819H25.8703V38.2981C25.2422 37.7304 23.9865 36.5388 23.9856 36.3108C25.9753 36.4055 30.0806 36.424 30.5832 35.7425C31.086 35.0612 33.5153 36.7842 34.6672 37.7307C36.238 37.4467 39.2539 36.3102 38.7512 34.0384L35.6096 30.9143C36.2379 28.6426 37.2434 23.7593 36.2385 22.3958ZM26.0168 19.0003C27.501 18.9886 28.9605 19.3463 30.2346 20.0345C31.5087 20.7226 32.5486 21.7147 33.2405 22.9016C33.9324 24.0886 34.2499 25.4249 34.1575 26.764C34.0651 28.103 33.5667 29.3934 32.717 30.4934L36.5432 33.8938C36.7908 34.1217 36.93 34.4289 36.9299 34.7483C36.9297 35.0676 36.7908 35.3742 36.5432 35.6018C36.2956 35.8295 35.9589 35.9605 35.6057 35.9661C35.2525 35.9716 34.9107 35.851 34.6545 35.6311L30.8264 32.2288C29.7957 32.9033 28.6028 33.3492 27.3459 33.5286C26.0892 33.708 24.8033 33.6165 23.593 33.262C22.3825 32.9075 21.2817 32.2992 20.3801 31.4876C19.4785 30.676 18.8008 29.6838 18.4035 28.5911C18.0063 27.4983 17.9007 26.3354 18.094 25.1985C18.2873 24.0619 18.7742 22.9825 19.5159 22.0481C20.2576 21.1137 21.2331 20.3501 22.3625 19.8206C23.4921 19.2911 24.7445 19.01 26.0168 19.0003ZM26.0403 21.4368C24.6109 21.4478 23.2446 21.9715 22.2424 22.8929C21.2403 23.8144 20.6843 25.0587 20.6965 26.3509C20.7088 27.643 21.2885 28.8776 22.3078 29.7835C23.3273 30.6893 24.7027 31.192 26.1321 31.1809C27.5615 31.1699 28.9278 30.6464 29.9299 29.7249C30.9321 28.8034 31.488 27.5591 31.4758 26.2669C31.4635 24.9748 30.8838 23.7401 29.8645 22.8343C28.8451 21.9285 27.4696 21.4258 26.0403 21.4368ZM26.1848 28.4651C26.5679 28.4652 26.881 28.7783 26.8811 29.1614C26.8811 29.5447 26.568 29.8586 26.1848 29.8587C25.8015 29.8587 25.4875 29.5447 25.4875 29.1614C25.4877 28.7782 25.8016 28.4651 26.1848 28.4651ZM26.1809 23.2376C26.9998 23.2376 27.5612 23.6105 27.8469 24.0774C28.0908 24.4782 28.234 25.2279 27.8577 25.7854C27.4395 26.4021 27.0385 26.5904 26.8225 26.9876C26.7355 27.1478 26.7004 27.2523 26.7004 27.7679H25.6936C25.6901 27.4961 25.648 27.0535 25.8606 26.6702C26.1289 26.1858 26.6449 25.8996 26.9446 25.471C27.2613 25.0214 27.0834 24.182 26.1848 24.1819C25.596 24.1819 25.3068 24.628 25.1848 24.9973L24.2815 24.6175C24.5289 23.8753 25.2018 23.2377 26.1809 23.2376Z"
//             fill="currentColor"
//           />
//         </svg>
//       </Box>
//       <Link to="/app/performance/assessment" style={{ textDecoration: 'none', width: '100%' }}>
//         <AdxButton variant="outlined" sx={{ width: '100%' }}>
//           Take Test
//         </AdxButton>
//       </Link>
//     </Box>
//   )
// }

const CalendarTodayDrawer = ({ date, data, state, handleDrawer }) => {
  const HeaderProps = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: 1,
        }}
      >
        <Typography variant="h3" color="dark-text.main" sx={{ fontWeight: 'bold', width: '220px' }}>
          {format(date, `EE, dd/MM/yyyy`)}
        </Typography>
      </Box>
    )
  }

  // const birthday = data.birthday
  // const holiday = data.holiday
  // const leaveRequests = data.leave_requests
  // const workAnniversary = data.workAnniversary

  return (
    <AdxDrawerComponent
      state={state}
      anchor={'right'}
      onClose={handleDrawer}
      noPaddingContent
      headerprops={<HeaderProps />}
    >
      {data ? (
        <Box>
          <Typography>Leave</Typography>

          {data.leave_request[0].staff_name}
        </Box>
      ) : (
        <AdxEmptyStateV2
          icon={<CalendarToday />}
          title="Nothing Interesting Today"
          subtitle={'Now go out there and make it happen!'}
        />
      )}
    </AdxDrawerComponent>
  )
}

const AnnouncementPanel = ({ data, navigate, isLoading, canCreate }) => {
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const announcements = data?.data || data?.announcements || (Array.isArray(data) ? data : [])
  const isArray = Array.isArray(announcements)
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [editTarget, setEditTarget] = useState(null)

  const { mutate: deleteAnnouncement } = useDeleteAnnouncement({
    onSuccess: () => setDeleteTarget(null),
    onError: (error) => console.error('Failed to delete announcement:', error),
  })

  const formatPostedAt = (dateStr) => {
    if (!dateStr) return ''

    const posted = new Date(dateStr)
    if (Number.isNaN(posted.getTime())) return ''

    const now = new Date()

    // Compare by local calendar day (midnight boundaries), not elapsed hours.
    const postedDay = new Date(posted.getFullYear(), posted.getMonth(), posted.getDate())
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    const diffDays = Math.round((today - postedDay) / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Posted today'
    if (diffDays === 1) return 'Posted 1 day ago'
    if (diffDays > 1 && diffDays <= 3) return `Posted ${diffDays} days ago`

    return `Posted at ${posted.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })}`
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.main',
        height: {
          xs: 'auto',
          xl: 'calc(100vh - 442px)',
        },
        borderRadius: '4.5px',
        border: 1,
        borderColor: 'grayscale.line-light',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: '20px',
          pt: '20px',
          pb: '12px',
          flexShrink: 0,
          mb: '24px',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Announcements
        </Typography>
        {canCreate && (
          <Box
            onClick={() => setIsCreateOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              color: 'alternate.main',
              '&:hover': { opacity: 0.8 },
            }}
          >
            <Typography sx={{ fontSize: '12px', color: 'primary.main', fontWeight: 500 }}>
              + Add
            </Typography>
          </Box>
        )}
      </Box>

      {/* List */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: '20px', pb: '8px' }}>
        {isLoading ? (
          [1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              sx={{
                borderLeft: '3px solid',
                borderColor: 'grayscale.line-light',
                pl: '10px',
                mb: '10px',
                py: '4px',
              }}
            >
              <Skeleton variant="text" width="80%" height={18} />
              <Skeleton variant="text" width="40%" height={14} />
            </Box>
          ))
        ) : isArray && announcements.length > 0 ? (
          announcements.map((item, index) => (
            <Box
              key={item.id || index}
              sx={{
                borderLeft: '3px solid #F3BF3A',
                pl: '10px',
                pr: '8px',
                mb: '8px',
                py: '8px',
                backgroundColor: 'grayscale.base-background',
                borderRadius: '10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                '&:hover': { backgroundColor: 'primary.light' },
                '&:hover .announcement-actions': { opacity: 1 },
              }}
              onClick={() => setSelectedAnnouncement(item)}
            >
              {/* Left: title + date */}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'text.primary',
                    lineHeight: 1.4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.title}
                </Typography>
                <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: '2px' }}>
                  {formatPostedAt(item.created_at || item.posted_at)}
                </Typography>
              </Box>

              {/* Edit/delete — only visible on hover, only for admins */}
              {canCreate && (
                <Box
                  className="announcement-actions"
                  sx={{
                    display: 'flex',
                    gap: '4px',
                    opacity: 0,
                    transition: 'opacity 0.15s ease',
                    flexShrink: 0,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      setEditTarget(item)
                    }}
                    sx={{
                      color: '#3A3A3A',
                      px: 0.5,
                      py: 0.5,
                      bgcolor: '#ffffff',
                      borderRadius: 1,
                      border: '1px solid #E0E0E0',
                      '&:hover': {
                        backgroundColor: '#FDF6E2',
                        borderColor: '#F3BF3A',
                      }
                    }}
                  >
                    {/* ...svg... */}
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 12 12" fill="none">
                      <path d="M3.3717 8.19432L5.4973 8.1871L10.1367 3.592C10.3188 3.40993 10.419 3.16813 10.419 2.91092C10.419 2.65371 10.3188 2.41192 10.1367 2.22985L9.37279 1.46592C9.00865 1.10178 8.37333 1.10371 8.01208 1.46448L3.3717 6.06054V8.19432ZM8.69171 2.147L9.45708 2.90948L8.68786 3.67147L7.92394 2.90803L8.69171 2.147ZM4.33504 6.46225L7.23949 3.58526L8.00341 4.34918L5.09944 7.22521L4.33504 7.22762V6.46225Z" fill="#3A3A3A" />
                      <path d="M2.40828 10.1153H9.15161C9.68289 10.1153 10.1149 9.68326 10.1149 9.15198V4.97689L9.15161 5.94023V9.15198H3.92938C3.91686 9.15198 3.90385 9.1568 3.89133 9.1568C3.87544 9.1568 3.85954 9.15246 3.84316 9.15198H2.40828V2.40865H5.70625L6.66958 1.44531H2.40828C1.877 1.44531 1.44495 1.87737 1.44495 2.40865V9.15198C1.44495 9.68326 1.877 10.1153 2.40828 10.1153Z" fill="#3A3A3A" />
                    </svg>
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeleteTarget(item)
                    }}
                    sx={{
                      color: '#3A3A3A',
                      px: 0.5,
                      py: 0.5,
                      bgcolor: '#ffffff',
                      borderRadius: 1,
                      border: '1px solid #E0E0E0',
                      '&:hover': {
                        backgroundColor: '#FDF6E2',
                        borderColor: '#F3BF3A',
                      }
                    }}
                  >
                    {/* ...svg... */}
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 12 12" fill="none">
                      <path d="M9.42495 2.35711C9.54993 2.35711 9.66979 2.40676 9.75817 2.49514C9.84655 2.58351 9.8962 2.70338 9.8962 2.82836C9.8962 2.95334 9.84655 3.07321 9.75817 3.16158C9.66979 3.24996 9.54993 3.29961 9.42495 3.29961H8.9537L8.95228 3.33307L8.51261 9.49278C8.49568 9.73056 8.38928 9.9531 8.21483 10.1156C8.04038 10.278 7.81085 10.3684 7.57246 10.3684H3.73696C3.49857 10.3684 3.26904 10.278 3.09459 10.1156C2.92014 9.9531 2.81374 9.73056 2.79682 9.49278L2.35714 3.33354L2.3562 3.29961H1.88495C1.75996 3.29961 1.6401 3.24996 1.55172 3.16158C1.46335 3.07321 1.4137 2.95334 1.4137 2.82836C1.4137 2.70338 1.46335 2.58351 1.55172 2.49514C1.6401 2.40676 1.75996 2.35711 1.88495 2.35711H9.42495ZM8.00978 3.29961H3.30011L3.73743 9.42586H7.57246L8.00978 3.29961ZM6.59745 0.943359C6.72243 0.943359 6.84229 0.993009 6.93067 1.08139C7.01905 1.16976 7.0687 1.28963 7.0687 1.41461C7.0687 1.53959 7.01905 1.65946 6.93067 1.74783C6.84229 1.83621 6.72243 1.88586 6.59745 1.88586H4.71245C4.58746 1.88586 4.4676 1.83621 4.37922 1.74783C4.29085 1.65946 4.2412 1.53959 4.2412 1.41461C4.2412 1.28963 4.29085 1.16976 4.37922 1.08139C4.4676 0.993009 4.58746 0.943359 4.71245 0.943359H6.59745Z" fill="#3A3A3A" />
                    </svg>
                  </IconButton>
                </Box>
              )}
            </Box>
          ))
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              py: 4,
            }}
          >
            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
              No announcements yet
            </Typography>
          </Box>
        )}
      </Box>

      {/* View All Footer */}
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'grayscale.line-light',
          px: '20px',
          py: '12px',
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Typography
          onClick={() => navigate('/app/announcements')}
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: 'alternate.main',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          View All
        </Typography>
      </Box>

      <CreateAnnouncementModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />

      <AnnouncementDetailModal
        open={!!selectedAnnouncement}
        announcement={selectedAnnouncement}
        onClose={() => setSelectedAnnouncement(null)}
      />

      <DeleteAnnouncementModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteAnnouncement(deleteTarget.id)}
        announcementTitle={deleteTarget?.title}
      />

      <EditAnnouncementModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        announcement={editTarget}
      />

    </Box>
  )
}
